import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  Users,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Download,
  ArrowLeft,
} from "lucide-react";
import "./giveFeedbackForLecturer.css";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";

const ViewReportPage = () => {
  const API_ENDPOINT = process.env.REACT_APP_API_END_POINT;
  const { reportId } = useParams();
  const [report, setReport] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getStatusStyle = (status, feedback) => {
    if (status === false || status === "false") {
      return {
        color: "#684e09",
        backgroundColor: " #ebca8e",
        fontWeight: "bold",
      };
    }

    const hasFeedback = feedback && feedback.trim() !== "";
    return {
      color: hasFeedback ? "#238070" : "#238070",
      backgroundColor: " #b7e7db",
      fontWeight: "bold",
    };
  };
  const getStatusLabel = (status, feedback) => {
    if (status === false || status === "false") return "Pending";

    const hasFeedback = feedback && feedback.trim() !== "";
    return hasFeedback ? "Reviewed with Feedback" : "Reviewed";
  };

  const handleExport = (reportId) => {
    axios
      .get(
        `${API_ENDPOINT}/api/ReviewerReports/ExportReportPdf?reportId=${reportId}`,
        {
          responseType: "blob",
          withCredentials: true,
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `report_${reportId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Report exported successfully");
      })
      .catch(() => {
        toast.error("Failed to export report");
      });
  };
  const handleDownloadRegister = async (reportId) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/api/ReviewerReports/DownloadAttendanceRegister?reportId=${reportId}`,
        {
          responseType: "blob",
          withCredentials: true,
        }
      );

      let filename = "register";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition
          .split("filename=")[1]
          .replace(/['"]/g, "")
          .trim();
      } else if (report.registerFileName) {
        filename = report.registerFileName;
      }

      const mimeType =
        response.data.type || response.headers["content-type"] || "";
      const blob = new Blob([response.data], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Register downloaded successfully");
    } catch {
      toast.error("No Register Attached");
    }
  };

  const fetchReport = useCallback(
    async (reportId) => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/api/ReviewerReports/ViewReport?reportId=${reportId}`,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response?.data?.status) {
          setReport(response.data.report);
          console.log(response.data.report);
        }
      } catch (error) {
        console.error("Failed to fetch report:", error);
      } finally {
        setLoading(false);
      }
    },
    [API_ENDPOINT]
  );

  useEffect(() => {
    if (reportId) fetchReport(reportId);
  }, [reportId, fetchReport]);

  const openfeedbackModal = async (message, reportId) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/api/ReviewerReports/ReviewReport`,
        { reportId: reportId, feedback: message },
        {
          withCredentials: true,
          validateStatus: () => true,
        }
      );
      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
        });
        fetchReport(reportId);
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
      console.error(error);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!report) return <p>Report not found.</p>;

  return (
    <main className="view-report-main">
      <header className="view-report-report-header ">
        <div
          className="view-report-header-content"
          style={{ display: "flex", alignItems: "center", gap: "12px" }}
        >
          <button
            onClick={() => navigate(-1)}
            className="back-arrow-btn"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              background: "none",
              border: "none",
              color: "#000000ff",
              fontWeight: "500",
              fontSize: "15px",
            }}
          >
            <ArrowLeft size={20} />
      
          </button>

          <h1 style={{ margin: 0 }}>{report.module}</h1>

          <div
            style={{ display: "flex", alignItems: "center", gap: "18px", marginLeft: "auto" }}
          >
            <p
              className="view-report-status-box"
              style={getStatusStyle(report.isViewed, report.feedback)}
            >
              {getStatusLabel(report.isViewed, report.feedback)}
            </p>
            <Download
              size={20}
              style={{ cursor: "pointer", color: "#238070" }}
              title="Export Report"
              onClick={() => handleExport(report.reportId)}
            />
          </div>
        </div>
        <p className="view-report-lecturer">
          Lecturer: {report.lecturerName} {report.lecturerSurname}
        </p>
        <p style={{ marginTop: "-1px", color: "#333", fontWeight: "500" }}>
          Module code: {report.moduleCode}
        </p>
        <p className="view-report-description">{report.description}</p>
      </header>

      <div className="view-report-report-grid">
        {/* All your existing cards remain unchanged */}
        {/* Date Information */}
        <div className="view-report-info-card">
          <div className="view-report-card-header">
            <Calendar size={20} />
            <h2>Date Information</h2>
          </div>
          <div className="view-report-card-content">
            <p>Start Date: {report.startDate}</p>
            <p>End Date: {report.endDate}</p>
            <p>Submitted: {report.submissionDate}</p>
          </div>
        </div>

        {/* Group Information */}
        <div className="view-report-info-card">
          <div className="view-report-card-header">
            <Users size={20} />
            <h2>Group Information</h2>
          </div>
          <div className="view-report-card-content">
            <p>Groups: {report.groupNames}</p>
          </div>
        </div>

        {/* Channel Activities */}
        <div className="view-report-channel-activities-section full-width">
          <div className="view-report-card-header">
            <MessageCircle size={20} />
            <h2>Channel Activities</h2>
          </div>
          <div className="view-report-channel-activities-row">
            {["WhatsApp", "Teams", "MYTUTOR"].map((channelName) => {
              const activity = report.channelActivities?.find(
                (a) =>
                  String(a.channelName).trim().toLowerCase() ===
                  channelName.trim().toLowerCase()
              );
              const activityText = activity?.activity ?? "—";
              return (
                <div
                  key={channelName}
                  className="view-report-info-card view-report-channel-card"
                >
                  <div className="view-report-channel-card-title">
                    {channelName}
                  </div>
                  <div className="view-report-card-content">
                    <p>{activityText}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sessions */}
        <div className="view-report-info-card full-width">
          <div className="view-report-card-header">
            <Calendar size={20} />
            <h2>Sessions</h2>
          </div>
          <div className="view-report-card-content">
            <div className="view-report-sessions-grid">
              {report.sessions?.map((session) => (
                <div key={session.sessionId} className="view-report-session-card">
                  <div className="view-report-session-header">
                    <h3>Session {session.sessionNumber}</h3>
                    <p className="view-report-date">{session.sessionDate}</p>
                  </div>
                  <div className="view-report-session-info">
                    <Users size={16} />
                    <span>{session.numberOfStudents} students</span>
                  </div>
                  <div className="view-report-session-info">
                    <strong>Group:</strong> {session.groupName}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Challenges */}
        <div className="view-report-info-card">
          <div className="view-report-card-header">
            <AlertCircle size={20} />
            <h2>Challenges</h2>
          </div>
          <div className="view-report-card-content">
            <p>{report.challenges}</p>
          </div>
        </div>

        {/* Suggestions */}
        <div className="view-report-info-card">
          <div className="view-report-card-header">
            <CheckCircle size={20} />
            <h2>Suggestions</h2>
          </div>
          <div className="view-report-card-content">
            <p>{report.suggestions}</p>
          </div>
        </div>

        {/* Weekly Activity */}
        <div className="view-report-info-card full-width">
          <div className="view-report-card-header">
            <Calendar size={20} />
            <h2>Weekly Activity</h2>
          </div>
          <div className="view-report-card-content">
            <p>{report.weeklyActivity}</p>
          </div>
        </div>

        {/* Lecturer Feedback */}
        <div className="view-report-feedback-review-card">
          <div className="view-report-card-header">
            <MessageCircle size={20} />
            <h2>Lecturer Feedback</h2>
          </div>
          <div className="view-report-card-content">
            <p>
              <strong>Reviewed By:</strong> {report.reviewedBy || "Not yet reviewed"}
            </p>
            <p>
              <strong>Feedback:</strong> {report.feedback || "No feedback provided."}
            </p>
          </div>
        </div>

        {/* Feedback & Download Buttons */}
        <div className="view-report-feedback-button-container">
          <button
            onClick={() => {
              Swal.fire({
                title: "Write FeedBack",
                input: "textarea",
                inputLabel: "Message",
                showCancelButton: true,
                confirmButtonText: `Send`,
                confirmButtonColor: "#16a34a",
                cancelButtonText: "Cancel",
                customClass: { popup: "my-swal-theme" },
              }).then(async (result) => {
                if (result.isConfirmed && result.value?.trim()) {
                  await openfeedbackModal(result.value, report.reportId);
                }
              });
            }}
            className="view-report-feedback-button"
          >
            Write Feedback
          </button>

          <button
            className="download-register-btn"
            onClick={() => handleDownloadRegister(report.reportId)}
          >
            Download Register
          </button>
        </div>
      </div>
    </main>
  );
};

export default ViewReportPage;
